import { Component, OnInit, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from '../../core/services/telemetry';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ONBOARDING_STEPS } from '../../core/config/onboarding-steps';

@Component({
    selector: 'app-webhook-setup',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './webhook-setup.component.html',
    styleUrls: ['./webhook-setup.component.scss']
})
export class WebhookSetupComponent implements OnInit {

    isLoading: boolean = false;
    currentUser: any = null;
    organizationId: string = '';
    organizationName: string = '';
    openFaqIndex: number | null = null;
    statusMessage: string = '';

    // Step progress tracking
    currentStep: number = 1;
    totalSteps: number = ONBOARDING_STEPS.length;
    progressPercentage: number = 0;

    private auth = inject(Auth);
    private authState$ = authState(this.auth);
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);
    private functions = inject(Functions);
    private readonly setupWebhooksFn = httpsCallable(this.functions, 'pcoSetupPlanningCenterWebhooks');
    private readonly syncInitialDataFn = httpsCallable(this.functions, 'pcoSyncInitialData');
    private onboardingService = inject(OnboardingService);

    webhookTypes = [
        {
            name: 'Lists',
            icon: 'bx-list-ul',
            description: 'Any lists that are created, updated, or deleted will refresh in Echo automatically'
        },
        {
            name: 'People',
            icon: 'bx-user',
            description: 'Member updates, additions, or deletions including contact details will reflect in Echo'
        },
        {
            name: 'Groups',
            icon: 'bx-group',
            description: 'Group creation, updates, deletions, and member changes will update on our side'
        }
    ];

    constructor(private router: Router) { }

    async ngOnInit(): Promise<void> {
        this.currentUser = await firstValueFrom(this.authState$);
        if (this.currentUser) {
            // Check if user is an owner - only owners can set up webhooks
            const userDocRef = doc(this.firestore, 'users', this.currentUser.uid);
            const userDocSnap = await runInInjectionContext(this.injector, () => getDoc(userDocRef));

            if (userDocSnap.exists()) {
                const userRole = userDocSnap.data()['role'];

                if (userRole !== 'owner') {
                    console.warn('User is not an owner - cannot set up webhooks');
                    await Swal.fire({
                        icon: 'info',
                        title: 'Owner Access Required',
                        html: 'Only organization owners can set up webhooks.<br><br>Your organization owner will complete this setup.',
                        confirmButtonText: 'Return to Dashboard'
                    });
                    this.router.navigate(['/']);
                    return;
                }
            }

            // Membership comes from the user document, not the Planning Center
            // sign-in claim: a standalone organisation has no such claim, and
            // reading it left organizationId empty, which then produced
            // "Invalid document reference" on every Firestore call below.
            const setup = await this.onboardingService.getSetupContext(
                this.currentUser.uid,
                'webhook-setup'
            );
            this.organizationId = setup.organizationId;
            this.organizationName = setup.organizationName;
            this.currentStep = setup.currentStep;
            this.totalSteps = setup.totalSteps;
            this.progressPercentage = setup.progressPercentage;
        }

    }

    async setupWebhooks() {
        // A second click used to set the webhooks up twice and start a second
        // sync that replaced the first. The button is disabled while loading,
        // but guard here too in case it is ever re-enabled early again.
        if (this.isLoading) return;
        this.isLoading = true;
        this.statusMessage = 'Connecting webhooks to Planning Center...';

        try {
            const result: any = await this.setupWebhooksFn({});

            console.log('Webhooks setup result:', result.data);

            // Webhooks connected – trigger initial data sync in background (async, non-blocking)
            this.syncInitialDataFn({}).then(() => {
                console.log('Background initial data sync complete');
            }).catch((syncError: any) => {
                // Sync failure is non-fatal – webhooks are still connected
                // The sync can be retried later or will catch up via webhooks
                console.warn('Background sync encountered an issue (non-fatal):', syncError.message);
            });

            // Stays loading through completeStep and the dialog: the page is
            // leaving, and re-enabling the button here is what let it be
            // clicked a second time.
            this.statusMessage = 'Finishing up...';

            // Mark webhook-setup as completed in onboarding state
            await this.onboardingService.completeStep(this.organizationId, 'webhook-setup');
            const nextStep = this.onboardingService.getNextIncompleteStep(['webhook-setup']);

            Swal.fire({
                icon: 'success',
                title: 'Webhooks Connected!',
                html: '<p>Echo is now synced with Planning Center and listening for real-time changes.</p>' +
                    '<p class="text-muted mb-0"><small><i class="bx bx-info-circle me-1"></i>Your Planning Center data (people, lists, groups, services) is syncing in the background. This may take a few minutes for large organizations.</small></p>',
                confirmButtonText: 'Continue'
            }).then(() => {
                this.router.navigate([nextStep ? nextStep.route : '/']);
            });

        } catch (error: any) {
            this.isLoading = false;
            this.statusMessage = '';
            console.error('Error setting up webhooks:', error);

            Swal.fire({
                icon: 'error',
                title: 'Webhook Setup Failed',
                text: error.message || 'Failed to set up webhooks. Please try again.',
                confirmButtonText: 'Retry'
            });
        }
    }

    toggleFaq(index: number) {
        this.openFaqIndex = this.openFaqIndex === index ? null : index;
    }
}
